import {
  ConfirmModal,
  Dots,
  EditPencil,
  OverflowTooltip,
  Tooltip,
  UserAvatar,
} from '@/components';
import { DeleteButton } from '@/components/DeleteButton/DeleteButton';
import { SortableDraggableTable } from '@/components/DraggableTable/SortableDraggableTable';
import { PeriodicityColumn } from '@/components/PeriodicityColumn/PeriodicityColumn';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { queryClient } from '@/constants';
import { RoutePath } from '@/core/router';
import { AutomationKey } from '@/modules/Automations/queries/keys';
import { BusinessAreasKey } from '@/modules/BusinessAreas/queries/keys';
import { CompanyKeys } from '@/modules/Companies/queries';
import { OrganizationKeys } from '@/modules/Organizations/queries/keys';
import {
  ProcessDetailsWorkflow,
  ProcessDetailsWorkflowTask,
} from '@/modules/Processes/domain';
import { ProcessKey } from '@/modules/Processes/queries';
import { EditTaskModal } from '@/modules/Tasks/components/EditTaskModal';
import { TaskKey, useDeleteTask } from '@/modules/Tasks/queries';
import { formatNumber, secondsToTimeDurationString } from '@/utils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DraggableWorkflowCollapse } from '../WorkflowCollapse/WorkflowCollapse';

type WorkflowTasksTableProps = {
  workflow: ProcessDetailsWorkflow;
  businessAreaId: number;
  isLoading: boolean;
};

export const WorkflowTasksTable = ({
  workflow,
  businessAreaId,
  isLoading,
}: WorkflowTasksTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedEditTaskId, setSelectedEditTaskId] = useState<number>();
  const [selectedDeleteTaskId, setSelectedDeleteTaskId] = useState<number>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const { mutate: deleteTask, isLoading: isDeletingTask } = useDeleteTask();

  const handleOpenRemoveTaskModalState = useCallback((id: number) => {
    setIsOpenDelete(true);
    setSelectedDeleteTaskId(id);
  }, []);

  const handleCloseRemoveTaskModalState = useCallback(() => {
    setIsOpenDelete(false);
    setSelectedDeleteTaskId(undefined);
  }, []);

  const handleDeleteTask = useCallback(() => {
    if (!selectedDeleteTaskId) {
      return;
    }

    deleteTask(selectedDeleteTaskId, {
      onError: () => {
        toast.error(t('error_deleting_task'));
      },
      onSuccess: () => {
        toast.success(t('success_deleting_task'));

        queryClient.invalidateQueries(TaskKey.ALL);
        queryClient.invalidateQueries(AutomationKey.ALL);
        queryClient.invalidateQueries(ProcessKey.ALL);
        queryClient.invalidateQueries(BusinessAreasKey.ALL);
        queryClient.invalidateQueries(CompanyKeys.ALL);
        queryClient.invalidateQueries(OrganizationKeys.ALL);
      },
    });

    handleCloseRemoveTaskModalState();
  }, [selectedDeleteTaskId, deleteTask, handleCloseRemoveTaskModalState, t]);

  const taskColumns = useMemo<SortableColumnDef<ProcessDetailsWorkflowTask>[]>(
    () => [
      {
        id: 'dots',
        name: '',
        renderRow: () => <Dots showBlueDot />,
        sortable: false,
        rowContainerClassNames: 'border-transparent',
        tdClassNames: 'w-6 min-w-6',
      },
      {
        id: 'name',
        name: t('name'),
        accessor: 'name',
        alignment: 'left',
        rowContainerClassNames: 'border-l rounded',
        tdClassNames: 'min-w-[12vw] max-w-[12vw] w-[12vw]',
        renderRow: (row) => {
          return (
            <div
              className="flex w-full items-center gap-2.5 overflow-hidden"
              onClick={() => navigate(RoutePath.taskDetail(row.id.toString()))}
            >
              <UserAvatar size="small" name={row.name} />
              <OverflowTooltip text={row.name} textClassName="font-black" />
            </div>
          );
        },
      },
      {
        id: 'code',
        name: t('code'),
        accessor: 'code',
        tdClassNames: 'min-w-[8vw] max-w-[8vw] w-[8vw]',
      },
      {
        id: 'status',
        name: t('status'),
        tdClassNames: 'max-w-fit',
        renderRow: (row) => t(row.status ? 'active' : 'inactive'),
      },
      {
        id: 'users',
        name: t('total_users'),
        accessor: 'usersCount',
        tdClassNames: 'max-w-fit',
      },
      {
        id: 'divider-1',
        name: '|',
        renderRow: () => '|',
        sortable: false,
      },
      {
        id: 'period',
        name: t('period'),
        accessor: 'periodicityId',
        tdClassNames: 'max-w-[10vw] w-[10vw]',
        renderRow: (row) =>
          row.periodicityId && (
            <PeriodicityColumn periodicity={row.periodicityId} />
          ),
        sortable: false,
      },
      {
        id: 'workload',
        name: t('workload_monthly'),
        tdClassNames: 'max-w-fit',
        renderRow: (row) => {
          return (
            <div className="overflow-hidden text-ellipsis">
              {secondsToTimeDurationString((row.workload ?? 0) * 60)}
            </div>
          );
        },
        sortable: false,
      },
      {
        id: 'estimated_cost_monthly',
        name: t('estimated_cost_monthly'),
        accessor: 'estimatedCost',
        tdClassNames: 'max-w-fit',
        renderRow: (row) => (
          <Tooltip showArrow content={`${formatNumber(row.estimatedCost, 4)}€`}>
            {formatNumber(row.estimatedCost)}€
          </Tooltip>
        ),
        sortable: false,
      },
      {
        id: 'divider-2',
        name: '|',
        renderRow: () => '|',
        sortable: false,
      },
      {
        id: 'automated',
        name: t('autom.'),
        accessor: 'automated',
        tdClassNames: 'max-w-fit',
        renderRow: (row) => (row.automated ? t('yes') : t('no')),
        sortable: false,
      },
      {
        id: 'actions',
        name: '',
        tdClassNames: 'w-20',
        alignment: 'right',
        renderRow: (row) => (
          <div className="flex h-full items-center gap-2 pr-2 text-gray-500 opacity-50">
            <button onClick={() => setSelectedEditTaskId(row.id)}>
              <EditPencil className="size-4" />
            </button>
            <DeleteButton
              onClick={() => handleOpenRemoveTaskModalState(row.id)}
              disabled={!row.canDelete || isDeletingTask}
              tooltip={t('delete_task')}
            />
          </div>
        ),
      },
    ],
    [handleOpenRemoveTaskModalState, isDeletingTask, navigate, t],
  );

  return (
    <>
      <DraggableWorkflowCollapse
        key={workflow.id}
        workflow={workflow}
        businessAreaId={businessAreaId}
        disableDrag={isLoading}
      >
        <SortableDraggableTable
          columns={taskColumns}
          rows={workflow.tasks}
          handlerIndex={taskColumns.length - 2}
          nonDraggableColumnKeys={['dots']}
          emptyStateMessage={t('no_tasks_available')}
          isDragSubmitting={isLoading}
        />
      </DraggableWorkflowCollapse>
      {!!selectedEditTaskId && (
        <EditTaskModal
          isOpen={!!selectedEditTaskId}
          id={selectedEditTaskId}
          handleClose={() => setSelectedEditTaskId(undefined)}
        />
      )}
      <ConfirmModal
        isOpen={isOpenDelete}
        handleClose={handleCloseRemoveTaskModalState}
        title={t('please_confirm_action')}
        message={t('confirm_task_delete')}
        onConfirm={handleDeleteTask}
      />
    </>
  );
};
